// MUI
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  CircularProgress,
  Grid,
  IconButton,
  Typography,
  Tooltip,
  Chip,
} from '@mui/material'

// Components
import Warning from 'components/molecules/Warning/Warning'

// Logic
import usePreferenceCardLogic from './PreferenceCard.logic'

// Icons
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'
import EditIcon from '@mui/icons-material/Edit'
// Constants
import {
  positioningDevices,
  positions,
  skinPreps,
  anesthesia,
} from 'views/PreferenceCards/CardView/constants'

// Styles
import styles from './PreferenceCard.module.scss'
import { LoadingButton } from '@mui/lab'
import { Medication } from 'views/PreferenceCards/CardView/CardView.types'

const PreferenceCard = () => {
  const {
    isLoading,
    consumables,
    flattenedArray,
    preferenceCard,
    accordionsToggle,
    filteredSkinPreps,
    isUnAssigning,
    filteredPositioningDevices,
    filteredAnesthesia,
    isLoadingConsumablesProductsData,
    renderGridItems,
    handleEditPreferenceCard,
    handleUnassignCard,
    setAccordionsToggle,
  } = usePreferenceCardLogic()

  return (
    <div className={styles['preference-card-container']}>
      <div className={styles['header-container']}>
        <div className={styles['title-container']}>
          <h3>
            {preferenceCard?.name
              ? `Preference Card: ${preferenceCard?.name}`
              : 'Preference Card'}
          </h3>
          <IconButton
            onClick={() => handleEditPreferenceCard(preferenceCard?.id)}
            disabled={isLoading || !preferenceCard}
          >
            <Tooltip title="Edit preference card">
              <EditIcon />
            </Tooltip>
          </IconButton>
        </div>
      </div>
      {!preferenceCard && !isLoading && (
        <div className={styles.warning}>
          <Warning text={'No preference card available.'} type="danger" />
        </div>
      )}
      {isLoading && (
        <div className={styles.loader}>
          <CircularProgress />
        </div>
      )}
      {preferenceCard && (
        <div className={styles['body-container']}>
          {/* Surgeon Information */}
          <Accordion className={styles.accordion} expanded>
            <AccordionSummary aria-controls="panel1-content" id="panel1-header">
              <Typography>Surgeon information</Typography>
            </AccordionSummary>
            <AccordionDetails className={styles['accordion-details']}>
              <div className={styles.row}>
                <div className={styles.col}>
                  <div className={styles.item}>
                    <div className={styles.label}>Surgeon Name</div>
                    <li className={styles.value}>
                      {preferenceCard?.surgeonInformation?.physicianName?.name}
                    </li>
                  </div>

                  <div className={styles.item}>
                    <div className={styles.label}>Procedure Name</div>
                    <li className={styles.value}>
                      {preferenceCard?.surgeonInformation?.procedureName}
                    </li>
                  </div>

                  {preferenceCard?.surgeonInformation?.dominantHand?.name && (
                    <div className={styles.item}>
                      <div className={styles.label}>Dominant Hand</div>
                      <li className={styles.value}>
                        {preferenceCard?.surgeonInformation?.dominantHand?.name}
                      </li>
                    </div>
                  )}

                  {preferenceCard?.surgeonInformation?.notes?.length ? (
                    <div className={styles.item}>
                      <div className={styles.label}>Other Notes</div>
                      {preferenceCard?.surgeonInformation?.notes?.map(
                        (note) => (
                          <li className={styles.value}>{note}</li>
                        )
                      )}
                    </div>
                  ) : null}
                </div>
                <div className={styles.col}>
                  {preferenceCard?.surgeonInformation?.picture && (
                    <div className={styles.item}>
                      <img
                        style={{ objectFit: 'cover' }}
                        className={styles.image}
                        src={preferenceCard?.surgeonInformation?.picture}
                        alt={
                          preferenceCard?.surgeonInformation?.physicianName
                            ?.name
                        }
                      />
                    </div>
                  )}
                  {preferenceCard?.surgeonInformation?.gloveSize && (
                    <div className={styles.item}>
                      <div className={styles.label}>Gloves size and type</div>
                      <li className={styles.value}>
                        {preferenceCard?.surgeonInformation?.gloveSize ??
                          'None'}
                      </li>
                      {preferenceCard?.surgeonInformation?.gloveType && (
                        <li className={styles.value}>
                          {preferenceCard?.surgeonInformation?.gloveType} glove
                        </li>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </AccordionDetails>
          </Accordion>

          {/* Procedure Information */}
          <Accordion
            className={styles.accordion}
            expanded={accordionsToggle.procedureInformation}
            onChange={(e, expanded) =>
              setAccordionsToggle({
                ...accordionsToggle,
                procedureInformation: expanded,
              })
            }
          >
            <AccordionSummary
              aria-controls="panel1-content"
              id="panel1-header"
              expandIcon={<ArrowDownwardIcon />}
            >
              <Typography>Procedure Information</Typography>
            </AccordionSummary>
            <AccordionDetails className={styles['accordion-details']}>
              <div className={styles.row}>
                <div className={styles.col}>
                  {preferenceCard?.procedureDetails?.selectedPosition && (
                    <div className={styles.item}>
                      <div className={styles.label}>Position</div>
                      <li className={styles.value}>
                        {preferenceCard?.procedureDetails?.selectedPosition
                          .other
                          ? preferenceCard?.procedureDetails?.selectedPosition
                              .other
                          : `${
                              positions[
                                preferenceCard?.procedureDetails
                                  ?.selectedPosition.index as number
                              ].name
                            } ${
                              preferenceCard?.procedureDetails?.selectedPosition
                                .side ?? ''
                            }`}
                      </li>
                    </div>
                  )}
                  {preferenceCard?.procedureDetails?.tableRotation &&
                    preferenceCard?.procedureDetails?.tableRotationSide && (
                      <div className={styles.item}>
                        <div className={styles.label}>Table Rotation</div>
                        <li className={styles.value}>
                          {`${
                            preferenceCard?.procedureDetails?.tableRotationSide
                          } ${
                            preferenceCard?.procedureDetails
                              ?.tableRotationDegree
                              ? ` - ${preferenceCard?.procedureDetails?.tableRotationDegree}°`
                              : ''
                          }`}
                        </li>
                      </div>
                    )}

                  {filteredSkinPreps?.length
                    ? preferenceCard?.procedureDetails?.skinPrep && (
                        <div className={styles.item}>
                          <div className={styles.label}>Skin Preps</div>
                          {filteredSkinPreps?.map(([key]) => (
                            <li>
                              {skinPreps.find((device) => device.id === key)
                                ?.name || key}
                            </li>
                          ))}
                        </div>
                      )
                    : null}

                  {preferenceCard?.procedureDetails?.medication && (
                    <div className={`${styles.item} ${styles.medication}`}>
                      <div className={styles.label}>Medications</div>
                      <ul className={`${styles.value} ${styles['no-margin']}`}>
                        {Object.entries(
                          preferenceCard.procedureDetails.medication
                        ).some(([_, medication]) => {
                          if (
                            typeof medication === 'object' &&
                            medication !== null
                          ) {
                            const med = medication as Medication
                            return Boolean(
                              med.concentration ||
                                med.epiConcentration ||
                                med.volume
                            )
                          }
                          return (
                            typeof medication === 'string' && medication !== ''
                          )
                        }) ? (
                          Object.entries(
                            preferenceCard.procedureDetails.medication
                          ).map(([medicationKey, medication]) => {
                            if (typeof medication === 'string') {
                              // For the 'other' field, directly render the value
                              return medication ? (
                                <li key={medicationKey}>
                                  <strong>
                                    {medicationKey.charAt(0).toUpperCase() +
                                      medicationKey
                                        .slice(1)
                                        .replace(/([A-Z])/g, ' $1')}
                                  </strong>
                                  : {medication}
                                </li>
                              ) : null
                            }

                            // For Medication fields, check and render their properties
                            const { concentration, epiConcentration, volume } =
                              medication as Medication
                            if (concentration || epiConcentration || volume) {
                              return (
                                <li key={medicationKey}>
                                  <strong>
                                    {medicationKey.charAt(0).toUpperCase() +
                                      medicationKey
                                        .slice(1)
                                        .replace(/([A-Z])/g, ' $1')}
                                  </strong>
                                  :
                                  <div>
                                    {concentration && (
                                      <div>Concentration: {concentration}</div>
                                    )}
                                    {epiConcentration && (
                                      <div>
                                        Epinephrine Concentration:{' '}
                                        {epiConcentration}
                                      </div>
                                    )}
                                    {volume && (
                                      <span style={{ marginLeft: '1rem' }}>
                                        Volume:{' '}
                                        <Chip
                                          size="small"
                                          color="info"
                                          label={volume}
                                        />
                                      </span>
                                    )}
                                  </div>
                                </li>
                              )
                            }
                            return null
                          })
                        ) : preferenceCard?.procedureDetails?.medication
                            ?.selectedMedications?.length ? null : (
                          <li>None</li>
                        )}
                      </ul>
                      {preferenceCard?.procedureDetails?.medication
                        ?.selectedMedications?.length ? (
                        <ul
                          className={`${styles.value} ${styles['no-margin']}`}
                        >
                          {preferenceCard?.procedureDetails?.medication?.selectedMedications?.map(
                            (medication) => (
                              <li key={medication.id}>
                                <div>{medication.name}</div>
                                <div
                                  style={{
                                    fontSize: '0.9em',
                                    color: '#666',
                                  }}
                                >
                                  <span>
                                    Timing:{' '}
                                    <Chip
                                      size="small"
                                      color="info"
                                      label={medication.timing}
                                    />
                                  </span>

                                  {medication.volume && (
                                    <span style={{ marginLeft: '1rem' }}>
                                      Volume:{' '}
                                      <Chip
                                        size="small"
                                        color="info"
                                        label={medication.volume}
                                      />
                                    </span>
                                  )}
                                </div>
                              </li>
                            )
                          )}
                        </ul>
                      ) : null}
                    </div>
                  )}

                  {preferenceCard?.procedureDetails?.DVTProhylaxis && (
                    <div className={styles.item}>
                      <div className={styles.label}>DVT Prohylaxis</div>
                      <li>
                        {preferenceCard?.procedureDetails?.DVTProhylaxis
                          ? preferenceCard?.procedureDetails?.dvtType
                            ? `${preferenceCard?.procedureDetails?.dvtType}${
                                preferenceCard?.procedureDetails?.anticoagSide
                                  ? ` - ${preferenceCard?.procedureDetails?.anticoagSide}`
                                  : ''
                              }`
                            : 'Yes'
                          : 'None'}
                      </li>
                    </div>
                  )}
                </div>

                <div className={styles.col}>
                  {preferenceCard?.procedureDetails?.selectedPosition &&
                    !preferenceCard?.procedureDetails?.selectedPosition
                      .other && (
                      <img
                        width={150}
                        className={styles.image}
                        src={
                          positions[
                            preferenceCard?.procedureDetails?.selectedPosition
                              ?.index as number
                          ]?.image
                        }
                        alt={
                          positions[
                            preferenceCard?.procedureDetails?.selectedPosition
                              ?.index as number
                          ]?.name
                        }
                      />
                    )}
                  {filteredPositioningDevices?.length
                    ? preferenceCard?.procedureDetails?.positioningDevices &&
                      preferenceCard?.procedureDetails?.positioningDevices
                        ?.otherText && (
                        <div className={styles.item}>
                          <div className={styles.label}>
                            Positioning Devices
                          </div>
                          {preferenceCard?.procedureDetails?.positioningDevices
                            ?.other &&
                            preferenceCard?.procedureDetails?.positioningDevices
                              ?.otherText && (
                              <li>
                                {
                                  preferenceCard?.procedureDetails
                                    ?.positioningDevices?.otherText
                                }
                              </li>
                            )}
                          {/* if other is true, show otherText value */}
                          {filteredPositioningDevices
                            ?.filter(
                              ([key]) => key !== 'otherText' && key !== 'other'
                            )
                            .map(([key]) => (
                              <li>
                                {positioningDevices.find(
                                  (device) => device.id === key
                                )?.name || key}
                              </li>
                            ))}
                        </div>
                      )
                    : null}
                  {preferenceCard?.procedureDetails?.notes?.length ? (
                    <div className={styles.item}>
                      <div className={styles.label}>Other Notes</div>
                      {preferenceCard?.procedureDetails?.notes?.map((note) => (
                        <li className={styles.value}>{note}</li>
                      ))}
                    </div>
                  ) : null}
                </div>
              </div>
            </AccordionDetails>
          </Accordion>

          {/* Equipment */}
          <Accordion
            className={styles.accordion}
            expanded={accordionsToggle.equipment}
            onChange={(e, expanded) =>
              setAccordionsToggle({ ...accordionsToggle, equipment: expanded })
            }
          >
            <AccordionSummary
              aria-controls="panel1-content"
              id="panel1-header"
              expandIcon={<ArrowDownwardIcon />}
            >
              <Typography>Equipment</Typography>
            </AccordionSummary>
            <AccordionDetails className={styles['accordion-details']}>
              <div className={styles.row}>
                <Grid container spacing={2}>
                  {flattenedArray?.length
                    ? renderGridItems(flattenedArray)
                    : null}
                </Grid>
                {preferenceCard?.equipment?.notes?.length ? (
                  <div className={styles.item}>
                    <div className={styles.label}>Other Notes</div>
                    {preferenceCard?.equipment?.notes?.map((note) => (
                      <li className={styles.value}>{note}</li>
                    ))}
                  </div>
                ) : null}
              </div>
            </AccordionDetails>
          </Accordion>

          {/* Instrument Trays */}
          <Accordion
            className={styles.accordion}
            expanded={accordionsToggle.instrumentTrays}
            onChange={(e, expanded) =>
              setAccordionsToggle({
                ...accordionsToggle,
                instrumentTrays: expanded,
              })
            }
          >
            <AccordionSummary
              aria-controls="panel1-content"
              id="panel1-header"
              expandIcon={<ArrowDownwardIcon />}
            >
              <Typography>Instrument Trays</Typography>
            </AccordionSummary>
            <AccordionDetails className={styles['accordion-details']}>
              <div className={styles.row}>
                <Grid container spacing={2}>
                  {preferenceCard?.instrumentTrays?.instruments?.map((item) => (
                    <ul>
                      <li>{item}</li>
                    </ul>
                  ))}
                </Grid>
                {preferenceCard?.instrumentTrays?.notes?.length ? (
                  <div className={styles.item}>
                    <div className={styles.label}>Other Notes</div>
                    {preferenceCard?.instrumentTrays?.notes?.map((note) => (
                      <li className={styles.value}>{note}</li>
                    ))}
                  </div>
                ) : null}
              </div>
            </AccordionDetails>
          </Accordion>

          {/* Implants */}
          <Accordion
            className={styles.accordion}
            expanded={accordionsToggle.implants}
            onChange={(e, expanded) =>
              setAccordionsToggle({ ...accordionsToggle, implants: expanded })
            }
          >
            <AccordionSummary
              aria-controls="panel1-content"
              id="panel1-header"
              expandIcon={<ArrowDownwardIcon />}
            >
              <Typography>Implants</Typography>
            </AccordionSummary>
            <AccordionDetails className={styles['accordion-details']}>
              <div className={styles.row}>
                {preferenceCard?.companies?.implants?.length ? (
                  <div className={styles.col}>
                    <div className={styles.item}>
                      <div className={styles.label}>Companies</div>
                      {preferenceCard?.companies?.implants?.map(({ name }) => (
                        <li className={styles.value}>{name}</li>
                      ))}
                    </div>
                  </div>
                ) : null}
                {preferenceCard?.products?.implants?.length ? (
                  <div className={styles.item}>
                    <div className={styles.label}>Products</div>
                    {preferenceCard?.products?.implants?.map((item) => (
                      <li className={styles.value}>{item.description}</li>
                    ))}
                  </div>
                ) : null}
              </div>
            </AccordionDetails>
          </Accordion>

          {/* Biologics */}
          <Accordion
            className={styles.accordion}
            expanded={accordionsToggle.biologics}
            onChange={(e, expanded) =>
              setAccordionsToggle({ ...accordionsToggle, biologics: expanded })
            }
          >
            <AccordionSummary
              aria-controls="panel1-content"
              id="panel1-header"
              expandIcon={<ArrowDownwardIcon />}
            >
              <Typography>Biologics</Typography>
            </AccordionSummary>
            <AccordionDetails className={styles['accordion-details']}>
              <div className={styles.row}>
                {preferenceCard?.companies?.biologic?.length ? (
                  <div className={styles.col}>
                    <div className={styles.item}>
                      <div className={styles.label}>Companies</div>
                      {preferenceCard?.companies?.biologic?.map(({ name }) => (
                        <li className={styles.value}>{name}</li>
                      ))}
                    </div>
                  </div>
                ) : null}
                {preferenceCard?.products?.biologic?.length ? (
                  <div className={styles.item}>
                    <div className={styles.label}>Products</div>
                    {preferenceCard?.products?.biologic?.map((item) => (
                      <li className={styles.value}>{item.description}</li>
                    ))}
                  </div>
                ) : null}
              </div>
            </AccordionDetails>
          </Accordion>

          {/* Anesthesia */}
          <Accordion
            className={styles.accordion}
            expanded={accordionsToggle.anesthesia}
            onChange={(e, expanded) =>
              setAccordionsToggle({
                ...accordionsToggle,
                anesthesia: expanded,
              })
            }
          >
            <AccordionSummary
              aria-controls="panel1-content"
              id="panel1-header"
              expandIcon={<ArrowDownwardIcon />}
            >
              <Typography>Anesthesia</Typography>
            </AccordionSummary>
            <AccordionDetails className={styles['accordion-details']}>
              <div className={styles.row}>
                {filteredAnesthesia?.length ? (
                  <div className={styles.col}>
                    <div className={styles.item}>
                      <div className={styles.label}>Types</div>
                      {filteredAnesthesia.map(([key]) => (
                        <li className={styles.value}>
                          {anesthesia.find((type) => type.id === key)?.name ||
                            key}
                        </li>
                      ))}
                    </div>
                  </div>
                ) : null}
              </div>
            </AccordionDetails>
          </Accordion>

          {/* Consumables */}
          <Accordion
            className={styles.accordion}
            expanded={accordionsToggle.consumables}
            onChange={(e, expanded) =>
              setAccordionsToggle({
                ...accordionsToggle,
                consumables: expanded,
              })
            }
          >
            <AccordionSummary
              aria-controls="panel1-content"
              id="panel1-header"
              expandIcon={<ArrowDownwardIcon />}
            >
              <Typography>Consumables</Typography>
            </AccordionSummary>
            <AccordionDetails className={styles['accordion-details']}>
              {isLoadingConsumablesProductsData ? (
                <div className={styles['consumables-loader']}>
                  <CircularProgress size={23} />
                </div>
              ) : consumables?.length ? (
                <div className={styles['consumables-container']}>
                  {/* Group items by tags */}
                  {(() => {
                    // Get all unique tags
                    const allTags = [
                      ...new Set(
                        consumables.flatMap((item) => item.tags || [])
                      ),
                    ]

                    // Items with tags
                    const taggedContent = allTags.map((tag) => {
                      const itemsWithTag = consumables.filter((item) =>
                        item.tags?.includes(tag)
                      )
                      if (itemsWithTag.length === 0) return null

                      return (
                        <div
                          key={tag}
                          className={styles['consumable-tag-group']}
                        >
                          <div className={styles['tag-header']}>
                            <Chip label={tag} color="info" size="small" />
                          </div>
                          {itemsWithTag.map((item) => (
                            <div
                              className={styles['consumable-item']}
                              key={`${tag}-${item?.item?.deviceDescription}`}
                            >
                              <div className={styles['consumable-content']}>
                                <span
                                  className={styles['consumable-description']}
                                >
                                  {item?.item?.deviceDescription}
                                </span>
                                <span className={styles['consumable-quantity']}>
                                  ({item?.qty})
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      )
                    })

                    // Items without tags
                    const taglessItems = consumables.filter(
                      (item) => !item.tags || item.tags.length === 0
                    )
                    const taglessContent =
                      taglessItems.length > 0 ? (
                        <div className={styles['consumable-tag-group']}>
                          <div className={styles['tag-header']}>
                            <Chip
                              label="General Items"
                              color="info"
                              size="small"
                            />
                          </div>
                          {taglessItems.map((item) => (
                            <div
                              className={styles['consumable-item']}
                              key={`tagless-${item?.item?.deviceDescription}`}
                            >
                              <div className={styles['consumable-content']}>
                                <span
                                  className={styles['consumable-description']}
                                >
                                  {item?.item?.deviceDescription}
                                </span>
                                <span className={styles['consumable-quantity']}>
                                  ({item?.qty})
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : null

                    return [...taggedContent, taglessContent]
                  })()}
                </div>
              ) : (
                <div className={styles['no-consumables']}>
                  No consumables available
                </div>
              )}
            </AccordionDetails>
          </Accordion>
        </div>
      )}

      {preferenceCard && (
        <LoadingButton
          variant="contained"
          color="error"
          size="small"
          fullWidth
          onClick={handleUnassignCard}
          loading={isUnAssigning}
        >
          Unassign Card
        </LoadingButton>
      )}
    </div>
  )
}

export default PreferenceCard
